import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';
import ChatPanel from '@components/chatpanel';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;

  padding-top: 5rem;

  min-height: 100vh;
  height: auto;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 var(--space-5);
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-xl);
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 var(--space-4);
    }
  }

  /* Optical alignment: at 80px Calibre's left side bearing puts the ink of
     "R"/"I" ~4.7px inside the box, against ~0.4-1.2px for the eyebrow and
     body copy, so the display lines read as indented. Pull them back by the
     difference, in em so it tracks the clamped font size. */
  h2.big-heading,
  h3.big-heading {
    margin-left: -0.03em;
  }

  h2.big-heading {
    color: var(--text);
    letter-spacing: -0.03em;
  }

  h3 {
    margin-top: var(--space-2);
    color: var(--text-muted);
    line-height: var(--lh-display);
    letter-spacing: -0.03em;
  }

  p {
    margin: var(--space-3) 0 0;
    max-width: var(--measure);
    color: var(--text-body);
  }
`;

const ChatWrapper = styled.div`
  position: relative; /* become the positioning context */
  width: 100%; /* fill the hero’s width */
  margin-top: var(--space-7);
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, [prefersReducedMotion]);

  const one = <h1>Hi, my name is</h1>;
  const two = <h2 className="big-heading">Rain Chong.</h2>;
  const three = <h3 className="big-heading">I research theoretical AGI.</h3>;
  const four = (
    <p>
      AI researcher and developer exploring the theoretical foundations of general intelligence. I
      am working on my <em className="emphasis">independent research</em> full time after leaving my
      role as Product &amp; AI Engineer at <a href="#jobs">Fellou AI</a>, formalizing a mathematical
      framework for cognition at the intersection of machine learning, cognitive psychology, and
      philosophy of mind.
    </p>
  );
  const five = (
    <ChatWrapper>
      <ChatPanel />
    </ChatWrapper>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection id="hero">
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={ChatWrapper}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
